import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@amzn/awsui-components-react';
import { responseToMarkdown } from './blocks';

export const COPY_AS_MARKDOWN_LABEL = 'Copy as markdown';
export const COPIED_TEXT = 'Copied';
export const COPY_FAILED_TEXT = 'Copy failed';
/** How long the confirmation stays visible before the button returns to idle. */
export const COPY_FEEDBACK_MS = 2000;

type CopyStatus = 'idle' | 'copied' | 'error';

/** execCommand fallback for contexts where the async clipboard API is missing or refuses the write. */
function copyViaTextarea(text: string): boolean {
  const active = document.activeElement as HTMLElement | null;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
    active?.focus?.();
  }
}

export async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or not a secure context: fall through to the legacy path.
    }
  }
  return copyViaTextarea(text);
}

/**
 * Copies an agent response to the clipboard as markdown.
 *
 * Holds its own status so a click re-renders only this button, not the memoized message bubble around it.
 */
export function CopyMessageButton({ content }: { content: string }) {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    const ok = await copyText(responseToMarkdown(content));
    setStatus(ok ? 'copied' : 'error');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus('idle'), COPY_FEEDBACK_MS);
  }, [content]);

  const iconName =
    status === 'copied' ? 'status-positive' : status === 'error' ? 'status-negative' : 'copy';

  return (
    <span className="rv-copy-message">
      {/* Visible text is the accessible name, so what is read matches what is shown. */}
      <Button variant="link" iconName={iconName} iconAlign="right" onClick={handleCopy}>
        {COPY_AS_MARKDOWN_LABEL}
      </Button>
      {/* The icon swap is the visual cue; this always-mounted live region is the spoken one. */}
      <span className="rv-sr-only" role="status" aria-live="polite">
        {status === 'copied' ? COPIED_TEXT : status === 'error' ? COPY_FAILED_TEXT : ''}
      </span>
    </span>
  );
}
